import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { Button } from "./Button";
import { EyebrowHeading, Section } from "./Section";
import { getRecaptchaSiteKey } from "@/lib/contact.functions";
import { submitCareer, type CareerRole, ROLE_LABELS_SV, ROLE_LABELS_EN } from "@/lib/career.functions";
import { track, trackConversion, trackFunnelStep } from "@/lib/analytics";
import { getAttribSnapshot } from "@/lib/analytics/track";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const SCRIPT_ID = "recaptcha-v3-script-career";

function useRecaptcha(siteKey: string | undefined) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!siteKey || typeof window === "undefined") return;
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => setReady(true));
      return;
    }
    if (document.getElementById(SCRIPT_ID)) return;
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => window.grecaptcha?.ready(() => setReady(true));
    document.head.appendChild(s);
  }, [siteKey]);
  return ready;
}

interface CareerFormProps {
  locale?: "sv" | "en";
  location?: string;
  partnerCtaId?: string;
}

const ROLES: CareerRole[] = [
  "spontan",
  "frilans",
  "partnerfotograf",
  "manus",
  "3d",
  "klipp",
  "regi",
  "casting",
  "post",
  "filmare",
];

export function CareerForm({ locale = "sv", location = "karriar", partnerCtaId }: CareerFormProps) {
  const en = locale === "en";
  const fetchKey = useServerFn(getRecaptchaSiteKey);
  const submit = useServerFn(submitCareer);
  const { data: keyData } = useQuery({
    queryKey: ["recaptcha-site-key"],
    queryFn: () => fetchKey(),
    staleTime: Infinity,
  });
  const siteKey = keyData?.siteKey;
  const ready = useRecaptcha(siteKey);

  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<CareerRole>("spontan");
  const formRef = useRef<HTMLFormElement>(null);
  const interactedRef = useRef(false);
  const search = useRouterState({ select: (state) => state.location.search });

  useEffect(() => {
    function selectPartnerPhotographer() {
      setRole("partnerfotograf");
      window.requestAnimationFrame(() => {
        formRef.current?.querySelector<HTMLInputElement>('input[name="visual_style"]')?.focus({ preventScroll: true });
      });
    }

    if (new URLSearchParams(window.location.search).get("role") === "partnerfotograf") {
      selectPartnerPhotographer();
    }
    const partnerCta = partnerCtaId ? document.getElementById(partnerCtaId) : null;
    partnerCta?.addEventListener("click", selectPartnerPhotographer);
    window.addEventListener("fenny:select-partner-photographer", selectPartnerPhotographer);
    return () => {
      partnerCta?.removeEventListener("click", selectPartnerPhotographer);
      window.removeEventListener("fenny:select-partner-photographer", selectPartnerPhotographer);
    };
  }, [partnerCtaId, search]);

  useEffect(() => {
    trackFunnelStep("career_form_view", { location, locale });
  }, [location, locale]);

  function handleFirstInteraction() {
    if (interactedRef.current) return;
    interactedRef.current = true;
    trackFunnelStep("career_form_first_interaction", { location, locale });
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    trackFunnelStep("career_form_submit_attempt", { location, locale, role });

    if (!siteKey || !window.grecaptcha || !ready) {
      setError(en ? "Verification isn't ready yet. Please try again in a moment." : "Verifieringen är inte redo ännu. Försök igen om en stund.");
      track("career_form_submit_error", { location, locale, reason: "recaptcha_not_ready" });
      return;
    }

    setStatus("sending");
    try {
      const fd = new FormData(e.currentTarget);
      const token = await window.grecaptcha.execute(siteKey, { action: "career" });
      const attrib = getAttribSnapshot();
      const res = await submit({
        data: {
          name: String(fd.get("name") ?? ""),
          email: String(fd.get("email") ?? ""),
          phone: String(fd.get("phone") ?? ""),
          city: String(fd.get("city") ?? ""),
          role,
          portfolio: String(fd.get("portfolio") ?? ""),
           visual_style: String(fd.get("visual_style") ?? ""),
          message: String(fd.get("message") ?? ""),
          locale,
          token,
          session_id: attrib.session_id,
          visitor_id: attrib.visitor_id,
          source_path: typeof window !== "undefined" ? window.location.pathname.slice(0, 255) : null,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
        },
      });
      if (res.ok) {
        setStatus("ok");
        formRef.current?.reset();
        trackConversion({
          conversion_type: "career_application_submit",
          location,
          label: "Career application",
          role,
        });
      } else {
        setStatus("error");
        setError(res.error);
        track("career_form_submit_error", { location, locale, role, reason: "server_error", message: res.error });
      }
    } catch (err) {
      console.error(err);
      setStatus("error");
      setError(en ? "Something went wrong. Please try again." : "Något gick fel. Försök igen.");
      track("career_form_submit_error", { location, locale, role, reason: "exception" });
    }
  }

  const roleLabels = en ? ROLE_LABELS_EN : ROLE_LABELS_SV;
  const isPartnerPhotographer = role === "partnerfotograf";

  return (
    <Section id={en ? "apply" : "ansok"} className="bg-surface/40">
      <div className="grid gap-12 md:grid-cols-2 md:items-start">
        <EyebrowHeading
          eyebrow={en ? "Apply" : "Ansök"}
          title={en ? "Tell us who you are" : "Berätta vem du är"}
          lede={
            en
              ? "Spontaneous application, freelance or specialist — we read everything and come back within a week when there is a match."
              : "Spontanansökan, frilans eller specialist — vi läser allt och återkommer inom en vecka när det finns en match."
          }
        />

        <form data-track-form="karriar"
          ref={formRef}
          onSubmit={onSubmit}
          onFocusCapture={handleFirstInteraction}
          onChangeCapture={handleFirstInteraction}
          className="rounded-3xl border border-white/10 bg-background p-6 md:p-8"
        >
          {status === "ok" ? (
            <div className="py-10 text-center">
              <div className="text-2xl font-semibold">{en ? "Thank you!" : "Tack!"}</div>
              <p className="mt-2 text-muted-foreground">
                {en ? "We have received your application and will be in touch." : "Vi har tagit emot din ansökan och hör av oss."}
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="space-y-4">
                <Field label={en ? "Name" : "Namn"} name="name" />
                <Field label={en ? "Email" : "E-post"} name="email" type="email" required />
                <Field label={en ? "Phone" : "Telefon"} name="phone" type="tel" />
                <Field
                  label={
                    isPartnerPhotographer
                      ? en ? "City / region" : "Ort / region"
                      : en ? "City" : "Ort"
                  }
                  name="city"
                  required={isPartnerPhotographer}
                />
              </div>

              <div>
                <span className="mb-2 block text-xs uppercase tracking-widest text-muted-foreground">
                  {en ? "What role are you interested in?" : "Vilken roll söker du?"}
                </span>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((r) => {
                    const active = role === r;
                    return (
                      <button
                        key={r}
                        type="button"
                        onClick={() => {
                          setRole(r);
                          handleFirstInteraction();
                        }}
                        aria-pressed={active}
                        className={
                          "rounded-full border px-3.5 py-1.5 text-sm transition-colors " +
                          (active
                            ? "border-mint bg-mint/15 text-mint"
                            : "border-white/10 bg-surface text-foreground/80 hover:border-mint/40 hover:text-foreground")
                        }
                      >
                        {roleLabels[r]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field
                label={
                  isPartnerPhotographer
                    ? en ? "Photo portfolio / image link" : "Fotoportfolio / bildlänk"
                    : en ? "Portfolio / link (optional)" : "Portfolio / länk (valfritt)"
                }
                name="portfolio"
                type="url"
                required={isPartnerPhotographer}
              />
              {isPartnerPhotographer && (
                <div className="space-y-3">
                  <Field
                    label={en ? "Visual style / niche / visual language" : "Visuell stil / nisch / formspråk"}
                    name="visual_style"
                    placeholder={
                      en
                        ? "For example documentary, editorial, minimal or warm/lifestyle"
                        : "Till exempel dokumentärt, redaktionellt, avskalat eller varmt/lifestyle"
                    }
                    required
                  />
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {en
                      ? "We match each assignment to the photographer whose personal style best fits the client's chosen direction. Tell us where in Sweden you work and link to images that represent your style and quality."
                      : "Vi matchar varje uppdrag med fotografen vars personliga stil passar kundens valda uttryck bäst. Ange var i Sverige du arbetar och länka till bilder som visar din stil och kvalitetsnivå."}
                  </p>
                </div>
              )}
              <Field
                label={en ? "Tell us briefly about yourself (optional)" : "Berätta kort om dig (valfritt)"}
                name="message"
                textarea
              />

              {error && (
                <p className="text-sm text-red-400" role="alert">
                  {error}
                </p>
              )}

              <Button type="submit" variant="mint" arrow className="w-full" disabled={status === "sending"}>
                {status === "sending"
                  ? en ? "Sending…" : "Skickar…"
                  : en ? "Send application" : "Skicka ansökan"}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                {en ? "Protected by reCAPTCHA. Google " : "Skyddat av reCAPTCHA. Google "}
                <a
                  href="https://policies.google.com/privacy"
                  className="underline underline-offset-2"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Privacy
                </a>{" "}
                &{" "}
                <a
                  href="https://policies.google.com/terms"
                  className="underline underline-offset-2"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Terms
                </a>
                .
              </p>
            </div>
          )}
        </form>
      </div>
    </Section>
  );
}

function Field({
  label,
  name,
  type = "text",
  textarea = false,
  required = false,
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  textarea?: boolean;
  required?: boolean;
  placeholder?: string;
}) {
  const base =
    "w-full rounded-2xl border border-white/10 bg-surface px-4 py-3 text-sm placeholder:text-muted-foreground focus:border-mint focus:outline-none focus:ring-2 focus:ring-mint/40";
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-widest text-muted-foreground">
        {label}
        {required && <span className="ml-1 text-mint">*</span>}
      </span>
      {textarea ? (
        <textarea name={name} rows={4} required={required} placeholder={placeholder} className={base} />
      ) : (
        <input type={type} name={name} required={required} placeholder={placeholder} className={base} />
      )}
    </label>
  );
}
